import React from 'react';
import {Component, Plugins, withSystem, Atom} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  EvaluatedNodeData,
  getChildren,
  getString,
  getInPojo,
} from '@packages/utilities/data-utils';
import {
  EBoolean,
  EElement,
  EEnum,
  EList,
  ELiteral,
  ENumber,
  ERecord,
  EText,
  TEnum,
  TListOfElement,
  TRecord,
} from '@packages/systems/wfdl-ast';
import {
  ERROR_STATE,
  UPDATE_ACCOUNT_ERROR_CODES,
  RESERVED_USER_FIELDS,
  updateAccountErrorStates,
  signUpErrorStates,
  SIGNUP_UI_ERROR_CODES,
  USYS_DATA_ATTRS,
} from '@packages/systems/users/constants';
import {communicationsText} from '../utils';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {Map} from 'immutable';

const UserAccountFormWrapper = Atom.createRender(
  ({attributes, classes, generateAttributes, data, renderChild}) => {
    const formState = getString(getInPojo(data, ['temp', 'state'])) ?? 'normal';

    const context = Map({formState});

    return (
      <div {...generateAttributes(attributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, context)
        )}
      </div>
    );
  }
);

const config = createIntegratedAtom({
  constraints: {
    descendants: [
      {
        is: 'UserAccountForm',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormErrorStateStyleVariant1',
        rule: 'ExactlyOne',
      },
      {
        is: 'UserFormSuccessState',
        rule: 'ExactlyOne',
      },
    ],
    page: [{type: 'UtilityPage', is: 'usys-user-account'}],
  },
  displayName: 'User Account Form Block',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Form'), iconName('FormBlock')),
  },
  render: UserAccountFormWrapper,
  showEmpty: true,
  fields: {
    temp: TRecord({
      state: withSystem(
        {label: 'State', defaultValue: 'normal', isFullWidth: true},
        TEnum({
          normal: 'Normal',
          success: 'Success',
          error: 'Error',
        })
      ),
    }),
    children: TListOfElement,
  },
});

export default config;

export const UserAccountFormPreset = EElement({
  id: 'User Account Form Wrapper',
  type: qualify(
    Plugins.name('Users'),
    Component.name('UserAccountFormWrapper')
  ),
  data: ERecord({
    temp: ERecord({
      state: EEnum('normal'),
    }),
    children: EList([
      EElement({
        id: 'User Account Form',
        type: qualify(Plugins.name('Users'), Component.name('UserAccountForm')),
        data: ERecord({
          temp: EList([]),
          type: EText('user-account'),
          children: EList([
            EElement({
              id: 'Email Label',
              type: qualify(
                Plugins.name('Form'),
                Component.name('FormBlockLabel')
              ),
              data: ERecord({
                form: ERecord({
                  type: EText('label'),
                  passwordPage: EBoolean(false),
                }),
                attr: ERecord({
                  for: EText('Email'),
                }),
                children: EList([
                  EElement({
                    id: 'Email String',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Email'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Form Email Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormEmailInput')
              ),
              data: ERecord({
                attr: ERecord({
                  disabled: EBoolean(true),
                  name: EText('Email'),
                  id: EText('wf-user-account-email'),
                  placeholder: EText(''),
                  autofocus: EBoolean(false),
                }),
              }),
            }),
            EElement({
              id: 'Name Label',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormBlockLabel')
              ),
              data: ERecord({
                users: ERecord({
                  field: EText(RESERVED_USER_FIELDS.name),
                }),
                attr: ERecord({
                  for: EText('Name'),
                }),
                children: EList([
                  EElement({
                    id: 'Name String',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Name'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Name Input',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserFormTextInput')
              ),
              data: ERecord({
                users: ERecord({
                  context: EEnum('userAccount'),
                  field: EText(RESERVED_USER_FIELDS.name),
                  fieldType: EText(''),
                }),
                attr: ERecord({
                  autofocus: EBoolean(false),
                  maxlength: ENumber(256),
                  type: EEnum('text'),
                  name: EText(''),
                  id: EText('wf-user-account-name'),
                  disabled: EBoolean(false),
                  required: EBoolean(true),
                  'data-name': EText(''),
                  placeholder: EText(''),
                  fieldType: EText(USYS_DATA_ATTRS.fieldType),
                }),
                form: ERecord({
                  type: EText('input'),
                  name: EText('name'),
                }),
              }),
            }),
            EElement({
              id: 'Password Heading',
              type: qualify(Plugins.name('Basic'), Component.name('Heading')),
              data: ERecord({
                tag: EEnum('h3'),
                children: EList([
                  EElement({
                    id: 'Password String',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Password settings'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Reset Password Link',
              type: qualify(Plugins.name('Basic'), Component.name('Link')),
              data: ERecord({
                button: EBoolean(false),
                block: EText('inline'),
                eventIds: EList([]),
                link: ELiteral({
                  name: qualify(Plugins.name('Basic'), typeName('Link')),
                  value: {
                    mode: 'external',
                    url: '/reset-password',
                  },
                }),
                children: EList([
                  EElement({
                    id: 'Reset Password String',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Reset password'),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Communications Checkbox Wrapper',
              type: qualify(
                Plugins.name('Form'),
                Component.name('FormCheckboxWrapper')
              ),
              data: ERecord({
                form: ERecord({
                  type: EText('checkbox'),
                }),
                children: EList([
                  EElement({
                    id: 'Communications Checkbox',
                    type: qualify(
                      Plugins.name('Users'),
                      Component.name('UserFormCheckboxInput')
                    ),
                    data: ERecord({
                      users: ERecord({
                        context: EEnum('userAccount'),
                        field: EText(RESERVED_USER_FIELDS.acceptCommunications),
                        fieldType: EText(''),
                      }),
                      attr: ERecord({
                        type: EText('checkbox'),
                        id: EText('wf-user-account-accept-communications'),
                        checked: EBoolean(false),
                        required: EBoolean(false),
                        name: EText(''),
                        'data-name': EText(''),
                        placeholder: EText(''),
                      }),
                      inputType: EEnum('default'),
                      form: ERecord({
                        type: EText('checkbox-input'),
                        name: EText(''),
                      }),
                    }),
                  }),
                  EElement({
                    id: 'Form Inline Label',
                    type: qualify(
                      Plugins.name('Form'),
                      Component.name('FormInlineLabel')
                    ),
                    data: ERecord({
                      form: ERecord({
                        type: EText('checkbox-label'),
                      }),
                      children: communicationsText,
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'User Account Form Save Button',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserAccountFormSaveButton')
              ),
              data: ERecord({
                attr: ERecord({
                  type: EText('submit'),
                  value: EText('Save Changes'),
                  'data-wait': EText('Saving...'),
                  disabled: EBoolean(false),
                }),
              }),
            }),
            EElement({
              id: 'User Account Form Cancel Button',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserAccountFormCancelButton')
              ),
              data: ERecord({
                attr: ERecord({
                  type: EText('reset'),
                  value: EText('Cancel'),
                  disabled: EBoolean(false),
                }),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Account Update Success Message',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormSuccessState')
        ),
        data: ERecord({
          children: EList([
            EElement({
              id: 'User Form Page Wrap Paragraph',
              type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
              data: ERecord({
                children: EList([
                  EElement({
                    id: 'User Form Page Wrap String 2',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('Your account was updated successfully.'),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      EElement({
        id: 'User Account Update Error State',
        type: qualify(
          Plugins.name('Users'),
          Component.name('UserFormErrorStateStyleVariant1')
        ),
        data: ERecord({
          users: ERecord({
            type: EText(ERROR_STATE.ACCOUNT_UPDATE),
          }),
          children: EList([
            EElement({
              id: 'User Account Update Error Msg',
              type: qualify(
                Plugins.name('Users'),
                Component.name('UserErrorMsg')
              ),
              data: ERecord({
                users: ERecord({
                  type: EText(ERROR_STATE.ACCOUNT_UPDATE),
                }),
                temp: ERecord({
                  index: ENumber(0),
                }),
                errors: EList([
                  ERecord({
                    id: EText(
                      updateAccountErrorStates[
                        UPDATE_ACCOUNT_ERROR_CODES.GENERAL_ERROR
                      ].id
                    ),
                    name: EText(
                      updateAccountErrorStates[
                        UPDATE_ACCOUNT_ERROR_CODES.GENERAL_ERROR
                      ].name
                    ),
                    copy: EText(
                      updateAccountErrorStates[
                        UPDATE_ACCOUNT_ERROR_CODES.GENERAL_ERROR
                      ].copy
                    ),
                  }),
                  ERecord({
                    id: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.VALIDATION_FAILED]
                        .id
                    ),
                    name: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.VALIDATION_FAILED]
                        .name
                    ),
                    copy: EText(
                      signUpErrorStates[SIGNUP_UI_ERROR_CODES.VALIDATION_FAILED]
                        .copy
                    ),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
    ]),
  }),
});
